import { useEffect, useState } from "react"
import { Combobox, CurrencyInput, EInputTypes, InputColumnTableProps, InputTable, InputTextCustom, formatCurrency, getDate } from "medere-ui-framework"
import '../Budget.scss'
import { EquipmentBudgetDetailDTO, defaultEquipmentBudgetDetailDTO, getSubtotalValueDay, getSubtotalValueMonth } from "../../../dto/budget/BudgetDetailDTO";
import { InputTableContainerProps } from "../../../dto/InputTableContainerProps";
import { NomenclatorDTO } from "../../../dto/NomenclatorDTO";
import React from "react";
import { getEquipments, getNomenclatorValue } from "../../../servicesHooks/Budget/useBudgetService";
import { BudgetBodyComponent } from "./BudgetBodyComponent";

interface EquipmentTableProps extends InputTableContainerProps {
    agreementplan: number;
    items: EquipmentBudgetDetailDTO[];
    date?: Date;
    healthEntity?: number;
    healthEntityPlan?: number;
}

export default function EquipmentTable({agreementplan, items, addToTotal, defaultEarnings, duration, CRUDBehaviour=true, showPriceByItem=true, showEarnings=true, date, healthEntity, healthEntityPlan}: EquipmentTableProps) {

    const [subtotalValueDay, setSubtotalValueDay] = useState<number>(0);
    const [subtotalValueMonth, setSubtotalValueMonth] = useState<number>(0);
    const [newEquipmentBudgetDetailDTO, setNewEquipmentBudgetDetailDTO] = useState<EquipmentBudgetDetailDTO>(defaultEquipmentBudgetDetailDTO);

    const equipmentSelectionEnabled = healthEntityPlan && (healthEntityPlan > 0) && date;
    const [editCostEnabled, setEditCostEnabled] = useState<boolean>(false);
    const [localEarnings, setLocalEarnings] = useState<number>(0);

    const calculateTotal = (unitsqty: number, unitprice: number, earning: number) => {
        return unitsqty * unitprice * (earning + 100) / 100;
    };

    function calculateItem(item: EquipmentBudgetDetailDTO): EquipmentBudgetDetailDTO {
        const totalMonth = calculateTotal(item.unitsqty, item.unitprice, item.earnings);
        return {
            ...item,
            valueday: duration ? ((duration > 0) ? (totalMonth / duration) : 0) : 0,
            valuemonth: totalMonth
        };
    }

    useEffect(() => {
        setNewEquipmentBudgetDetailDTO(prev => ({...prev, valueday: duration ? ((duration > 0) ? prev.valuemonth / duration : 0) : 0}));
        let subtotal = 0;
        items.forEach((item) => {
            item.valuemonth = calculateTotal(item.unitsqty, item.unitprice, item.earnings);
            item.valueday = duration ? ((duration > 0) ? item.valuemonth / duration : 0) : 0;
            subtotal += item.valuemonth;
        });
        setSubtotalValueMonth(subtotal);
    }, [items, duration]);

    useEffect(() => {
        setLocalEarnings(defaultEarnings ?? 0);
        setNewEquipmentBudgetDetailDTO(prev => ({...prev, earnings: defaultEarnings ?? 0}));
    }, [defaultEarnings]);

    useEffect(() => {
        if (newEquipmentBudgetDetailDTO.item > 0 && equipmentSelectionEnabled)
            getNomenclatorValue(newEquipmentBudgetDetailDTO.item ?? -1, getDate(date) ?? undefined, healthEntityPlan).then((value: number) => {
                setEditCostEnabled(!value);
                setNewEquipmentBudgetDetailDTO(prev => ({...prev, unitprice: value ?? 0}));
            });
    }, [newEquipmentBudgetDetailDTO.item, date, healthEntityPlan]);

    useEffect(() => {
        setNewEquipmentBudgetDetailDTO(prev => ({
            ...prev,
            valuemonth: calculateTotal(prev.unitsqty, prev.unitprice, prev.earnings)
        }));
    }, [newEquipmentBudgetDetailDTO.unitsqty, newEquipmentBudgetDetailDTO.unitprice, newEquipmentBudgetDetailDTO.earnings]);

    useEffect(() => {
        setNewEquipmentBudgetDetailDTO(prev => ({...prev, valueday: duration ? ((duration > 0) ? prev.valuemonth / duration : 0) : 0}));
    }, [newEquipmentBudgetDetailDTO.valuemonth, duration]);

    useEffect(() => {
        setSubtotalValueDay(duration ? ((duration > 0) ? subtotalValueMonth / duration : 0) : 0);
    }, [subtotalValueMonth, duration]);

    const columnsTable: InputColumnTableProps<EquipmentBudgetDetailDTO>[] = React.useMemo(() => [{
        body: (item) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        className: "columnStart",
        header: "Item",
        input: (_item) =>
            <Combobox
                getItems={getEquipments}
                params={{healthEntity}}
                optionLabel="codeanddescription"
                minPanelWidth={600}
                maxPanelWidth={800}
                placeholder={equipmentSelectionEnabled ? "Seleccionar" : "Fecha y Convenio obligatorios"}
                setValue={(e: NomenclatorDTO) => {
                    if (e) {
                        setNewEquipmentBudgetDetailDTO(prev => ({
                            ...prev,
                            item: e.nomenclator ?? -1,
                            description: e.codeanddescription ?? '',
                        }));
                    }
                }}
                value={newEquipmentBudgetDetailDTO.description}
                disable={!equipmentSelectionEnabled}
            />,
        footer: "Subtotales",
        footerClassName: "font-bold",
    }, {
        className: "tableColumn justify-content-center",
        header: "Unidades",
        input: (_item) =>
            <InputTextCustom
                value={newEquipmentBudgetDetailDTO.unitsqty}
                inputType={EInputTypes.NUMBER}
                inputClassName="text-center"
                onChange={(e: any) => {
                    setNewEquipmentBudgetDetailDTO(prev => ({...prev, unitsqty: e.target.value ?? 0}));
                }}
            />,
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.unitsqty} inputClassName="text-center" onChange={(e: any) => {
                onHandleEditBudget(index, calculateItem({...item, unitsqty: e.target.value ?? 0}));
            }} />
    }, {
        className: "columnPrice",
        header: <p className="mr-2">{showEarnings ? "Costo /Convenio" : "Valor unitario"}</p>,
        input: (_item) =>
            <React.Fragment>
                {!editCostEnabled &&
                    <p className="mr-2">{formatCurrency(newEquipmentBudgetDetailDTO.unitprice as number)}</p>}
                {editCostEnabled &&
                    <CurrencyInput ammount={newEquipmentBudgetDetailDTO.unitprice} setAmmount={(newValue: number | null) => {
                        setNewEquipmentBudgetDetailDTO(prev => ({...prev, unitprice: newValue ?? prev.unitprice ?? 0}));
                    }} />}
            </React.Fragment>,
        bodyClassName: "justify-content-end",
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} currency value={item.unitprice} pClassName="mr-2" pValue={showPriceByItem ? formatCurrency(item.unitprice * (showEarnings ? 1 : (item.earnings + 100) / 100) as number) : ''} onChange={(newValue: number | null) => {
                onHandleEditBudget(index, calculateItem({...item, unitprice: newValue ?? 0}));
            }} />
    }, {
        className: "tableColumn justify-content-center",
        header: "% Ganancia",
        input: (_item) =>
            <InputTextCustom
                value={localEarnings}
                inputType={EInputTypes.NUMBER}
                inputClassName="text-center"
                onChange={(e: any) => {
                    setLocalEarnings(e.target.value ?? 0);
                    setNewEquipmentBudgetDetailDTO(prev => ({...prev, earnings: e.target.value ?? 0}));
                }}
            />,
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.earnings} inputClassName="text-center" onChange={(e: any) => {
                onHandleEditBudget(index, calculateItem({...item, earnings: e.target.value ?? 0}));
            }} />,
        show: () => showEarnings
    }, {
        className: "columnPrice w-8",
        header: "Valor día",
        input: (_item) => <p>{formatCurrency(newEquipmentBudgetDetailDTO.valueday as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem ? formatCurrency(item.valueday as number) : '',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueDay as number),
        show: () => duration > 0
    }, {
        className: `columnPrice ${showEarnings ? 'w-10' : 'w-8'} pr-2`,
        header: "Valor total",
        input: (_item) => <p>{formatCurrency(newEquipmentBudgetDetailDTO.valuemonth as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem ? formatCurrency(item.valuemonth as number) : '',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueMonth as number)
    }] as InputColumnTableProps<EquipmentBudgetDetailDTO>[], [duration, newEquipmentBudgetDetailDTO, subtotalValueDay, subtotalValueMonth, showPriceByItem, showEarnings, editCostEnabled, equipmentSelectionEnabled]);

    function onHandleAddBudget(): void {
        if (newEquipmentBudgetDetailDTO.item === -1) return;
        if (newEquipmentBudgetDetailDTO.unitsqty === 0) return;
        items.push(newEquipmentBudgetDetailDTO);
        const updatedValueMonth = getSubtotalValueMonth(items);
        if (addToTotal)
            addToTotal(updatedValueMonth - subtotalValueMonth);
        setSubtotalValueMonth(updatedValueMonth);
        setSubtotalValueDay(getSubtotalValueDay(items));
        setNewEquipmentBudgetDetailDTO({...defaultEquipmentBudgetDetailDTO, earnings: localEarnings ?? defaultEarnings ?? 0});
    }

    function onHandleDeleteBudget(index: number): void {
        items.splice(index, 1);
        const updatedValueMonth = getSubtotalValueMonth(items);
        if (addToTotal)
            addToTotal(updatedValueMonth - subtotalValueMonth);
        setSubtotalValueMonth(updatedValueMonth);
        setSubtotalValueDay(getSubtotalValueDay(items));
    }

    function onHandleEditBudget(index: number, newItemValue: EquipmentBudgetDetailDTO): void {
        const valueMonthToAdd = newItemValue.valuemonth - items[index].valuemonth;
        items.splice(index, 1, newItemValue);
        const updatedValueMonth = subtotalValueMonth + valueMonthToAdd;
        setSubtotalValueDay(duration ? ((duration > 0) ? (updatedValueMonth / duration) : 0) : 0);
        setSubtotalValueMonth(updatedValueMonth);
        if (addToTotal) addToTotal(valueMonthToAdd);
    }

    return (
        <div className="nv-card budget-table-card">
            <span className="tableTitle"> Equipamiento </span>
            <InputTable divider dividerClassName="my-0 border-top-1" dividerInFirstItem columns={columnsTable} items={items} onAdd={CRUDBehaviour ? onHandleAddBudget : undefined} onDelete={CRUDBehaviour ? (_item, index) => onHandleDeleteBudget(index) : undefined} headerDivClassName="tableHeader align-items-center" inputDivClassName={"align-items-center"} footerDivClassName="tableFooter" divBtnClassName="max-w-54 min-w-54" />
        </div>
    );
}
